import { useNavigate } from 'react-router-dom';
import { ClipboardList, AlertCircle, Clock, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow, isPast, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { usePortalAssessments } from '@/api/hooks/usePortal';
import { cn } from '@/lib/utils';
import type { PortalAssessment } from '@/api/types/assessment.types';

const STATUS_ORDER: Record<string, number> = {
  issued: 0, in_progress: 1, sent_back: 2, submitted: 3,
  under_review: 4, approved: 5, rejected: 6, cancelled: 7, expired: 8,
};

function AssessmentRow({ assessment }: { assessment: PortalAssessment }) {
  const navigate = useNavigate();
  const overdue = assessment.due_date ? isPast(parseISO(assessment.due_date)) : false;
  const isComplete = ['submitted', 'under_review', 'approved'].includes(assessment.status);
  const progress = assessment.total_questions > 0
    ? Math.round((assessment.answered_count / assessment.total_questions) * 100)
    : 0;

  function cta(): string {
    if (assessment.status === 'approved') return 'View';
    if (assessment.status === 'submitted' || assessment.status === 'under_review') return 'View Submission';
    if (assessment.answered_count === 0) return 'Start';
    return 'Continue';
  }

  return (
    <Card className={cn('transition-shadow hover:shadow-md', overdue && !isComplete && 'border-accent-rose/30')}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Info */}
          <div className="flex-1 space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-text-secondary">{assessment.tenant_name}</p>
              <StatusBadge status={assessment.status} />
              {overdue && !isComplete && (
                <Badge variant="rose" className="text-[10px]">Overdue</Badge>
              )}
            </div>
            <p className="font-semibold text-text-primary">{assessment.title}</p>
          </div>

          {/* Due / submitted date */}
          <div className="shrink-0 text-sm">
            {isComplete && assessment.submitted_at
              ? <div className="flex items-center gap-1.5 text-text-secondary">
                  <CheckCircle2 className="h-4 w-4 text-accent-emerald" />
                  Submitted {formatDistanceToNow(parseISO(assessment.submitted_at), { addSuffix: true })}
                </div>
              : assessment.due_date
                ? <div className={cn('flex items-center gap-1.5', overdue ? 'text-accent-rose' : 'text-text-secondary')}>
                    {overdue ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                    Due {formatDistanceToNow(parseISO(assessment.due_date), { addSuffix: true })}
                  </div>
                : null
            }
          </div>

          {/* Progress */}
          {!isComplete && (
            <div className="shrink-0 w-24 space-y-1">
              <div className="flex justify-between text-xs text-text-secondary">
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-bg-surface-2">
                <div
                  className={cn('h-full rounded-full', progress === 100 ? 'bg-accent-emerald' : 'bg-accent-cyan')}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <Button
            size="sm"
            variant={isComplete ? 'outline' : 'default'}
            onClick={() =>
              isComplete
                ? navigate(`/portal/assessments/${assessment.id}/submitted`)
                : navigate(`/portal/assessments/${assessment.id}`)
            }
            className="shrink-0"
          >
            {cta()}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function PortalAssessmentListPage() {
  const { data: assessments, isLoading } = usePortalAssessments();

  const sorted = [...(assessments ?? [])].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-text-secondary" />
        <h1 className="text-lg font-bold text-text-primary">Assessments</h1>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center gap-3">
            <ClipboardList className="h-10 w-10 text-text-secondary/40" />
            <p className="text-sm text-text-secondary">No assessments assigned yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((a) => <AssessmentRow key={a.id} assessment={a} />)}
        </div>
      )}
    </div>
  );
}
